import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

const FLY_LAUNCH_STAGGER = 70;
const FLY_IMAGE_DECODE_TIMEOUT = 1200;
const flyImageDecodeCache = new Map();
let flyLaunchQueue = Promise.resolve();

/**
 * FlyToCart custom element.
 *
 * Straight-line fly animation:
 * - no curved X/Y split
 * - no animation-composition
 * - exact final alignment with destinationPoint
 * - slower overall
 * - starts slower and accelerates toward the end via CSS timing
 * - emits fly-to-cart:done when finished so cart bubble animation can sync
 */
class FlyToCart extends Component {
  /** @type {{ x: number, y: number } | null} */
  sourcePoint = null;

  /** @type {{ x: number, y: number } | null} */
  destinationPoint = null;

  /** @type {DOMRect | null} */
  sourceRect = null;

  /** @type {DOMRect | null} */
  destinationRect = null;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element | null} */
  source = null;

  /** @type {Element | null} */
  destination = null;

  #finished = false;

  connectedCallback() {
    super.connectedCallback();
    this.#queueAnimation();
  }

  #queueAnimation = () => {
    const flight = this.#getFlightData();

    if (!flight) {
      this.#finish();
      return;
    }

    const launchTask = flyLaunchQueue.then(async () => {
      await this.#prepareAndStart(flight);
      await delay(FLY_LAUNCH_STAGGER);
    });

    flyLaunchQueue = launchTask.catch(() => {});
  };

  #getFlightData() {
    const sourceRect =
      this.sourceRect ??
      (this.source ? this.source.getBoundingClientRect() : null);

    const destinationRect =
      this.destinationRect ??
      (this.destination ? this.destination.getBoundingClientRect() : null);

    const startPoint =
      this.sourcePoint ??
      (sourceRect?.width && sourceRect?.height
        ? {
            x: sourceRect.left + sourceRect.width / 2,
            y: sourceRect.top + sourceRect.height / 2,
          }
        : null);

    const endPoint =
      this.destinationPoint ??
      (destinationRect?.width && destinationRect?.height
        ? {
            x: destinationRect.left + destinationRect.width / 2,
            y: destinationRect.top + destinationRect.height / 2,
          }
        : null);

    if (!startPoint || !endPoint) {
      return null;
    }

    const travelX = endPoint.x - startPoint.x;
    const travelY = endPoint.y - startPoint.y;
    const distance = Math.hypot(travelX, travelY);

    /**
     * Slower straight-line duration:
     * - short homepage flights remain readable
     * - long carousel flights do not teleport
     * - capped to avoid feeling broken
     */
    const duration = clamp(distance / 420, 0.95, 2.35);
    return {
      duration,
      sourceRect,
      startPoint,
      travelX,
      travelY,
    };
  }

  #prepareAndStart = async (flight) => {
    const { duration, sourceRect, startPoint, travelX, travelY } = flight;

    if (!this.isConnected) {
      this.#finish();
      return;
    }

    if (this.useSourceSize && sourceRect?.width && sourceRect?.height) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }

    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${travelX}px`);
    this.style.setProperty('--travel-y', `${travelY}px`);
    this.style.setProperty('--fly-duration', `${duration}s`);

    await waitForBackgroundImage(this);
    await nextAnimationFrame();
    await yieldToMainThread();

    if (!this.isConnected) {
      this.#finish();
      return;
    }

    this.classList.add('fly-to-cart--ready');
    this.#finishAfterAnimation(duration);
  };

  #finishAfterAnimation = async (duration) => {
    const animations = this.getAnimations();

    if (!animations.length) {
      setTimeout(() => {
        this.#finish();
      }, Math.ceil(duration * 1000) + 120);

      return;
    }

    await Promise.allSettled(animations.map((animation) => animation.finished));
    this.#finish();
  };

  #finish = () => {
    if (this.#finished) return;
    this.#finished = true;

    this.dispatchEvent(
      new CustomEvent('fly-to-cart:done', {
        bubbles: true,
      })
    );

    this.remove();
  };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function nextAnimationFrame() {
  return new Promise((resolve) => requestAnimationFrame(resolve));
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Waits for the fly thumbnail to be ready so the animation doesn't start with
 * only the fallback background painted.
 * @param {HTMLElement} element
 */
async function waitForBackgroundImage(element) {
  const imageUrl = getBackgroundImageUrl(element.style.backgroundImage);
  if (!imageUrl) return;

  await preloadFlyImage(imageUrl);
}

/**
 * @param {string} backgroundImage
 * @returns {string | null}
 */
function getBackgroundImageUrl(backgroundImage) {
  const value = backgroundImage?.trim();
  if (!value || value === 'none' || !value.startsWith('url(')) return null;

  let url = value.slice(4, -1).trim();
  const quote = url[0];

  if ((quote === '"' || quote === "'") && url.charAt(url.length - 1) === quote) {
    url = url.slice(1, -1);
  }

  return url.replace(/\\(["'])/g, '$1');
}

/**
 * @param {string} src
 * @returns {Promise<boolean>}
 */
function preloadFlyImage(src) {
  const cached = flyImageDecodeCache.get(src);
  if (cached) return cached;

  const promise = new Promise((resolve) => {
    const image = new Image();
    let settled = false;

    const finish = (loaded) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      resolve(loaded);
    };

    const decode = async () => {
      try {
        await image.decode?.();
      } catch {
        // The image can still be paintable if decode() rejects after load.
      }

      finish(image.naturalWidth > 0);
    };

    const timeout = setTimeout(() => finish(false), FLY_IMAGE_DECODE_TIMEOUT);

    image.decoding = 'async';
    image.onload = decode;
    image.onerror = () => finish(false);
    image.src = src;

    if (image.complete) {
      decode();
    }
  });

  flyImageDecodeCache.set(src, promise);
  promise.then((loaded) => {
    if (!loaded) flyImageDecodeCache.delete(src);
  });

  return promise;
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
